import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';

/**
 * "Do not miss this" — the band that asks a shopper for an address:
 *
 *   <NewsletterBand className="max-w-[598px]">
 *     <NewsletterBandCopy>
 *       <NewsletterBandTitle>Tady nesmíte chybět!</NewsletterBandTitle>
 *       <NewsletterBandSubtitle>Odebírejte zprávy…</NewsletterBandSubtitle>
 *     </NewsletterBandCopy>
 *     <NewsletterBandForm noValidate onSubmit={handleSubmit(onSubmit)}>
 *       <NewsletterBandField>
 *         <NewsletterBandInput variant={…} {...register('email')} />
 *         <NewsletterBandFieldMark><IconEnvelopeOpen /></NewsletterBandFieldMark>
 *       </NewsletterBandField>
 *       <NewsletterBandSubmit loading={isPending}>Odebírat</NewsletterBandSubmit>
 *     </NewsletterBandForm>
 *     <NewsletterBandConsent>Registrací potvrzujete…</NewsletterBandConsent>
 *     {isSubmitted ? <NewsletterBandNotice variant={…}>…</NewsletterBandNotice> : null}
 *   </NewsletterBand>
 *
 * The one part of a footer both storefronts on this platform carry in the same
 * shape: a line of copy, a field with a mark in it, a submit fused to that
 * field's right edge, a consent line, and whatever the submit came back with.
 * rcprofi sits it in a two-column grid beside its support block and RacingLine
 * runs it full width as a band of its own — which is where it stands, so no
 * prop decides it and the measure rides `className`.
 *
 * **Nothing about subscribing is here.** The address schema, the resolver, the
 * mutation, the error capture and every string are the tenant's; what this
 * section knows is that a field and its submit read as one control, and that
 * an invalid address says so on the field rather than beside it.
 *
 * Server-first: every part is a plain component, and the `'use client'` the
 * form needs sits on the tenant's own leaf around them.
 */

/**
 * The stack: copy, field row, consent, outcome.
 *
 * It sets the rhythm between them and nothing else — how wide the band runs is
 * the page's, because that is exactly what the two tenants disagree about.
 */
const newsletterBandClasses = 'flex flex-col gap-y-2';

export type NewsletterBandProps = ComponentProps<'div'>;

/** The newsletter band. */
export const NewsletterBand = ({ className, ...props }: NewsletterBandProps) => (
  <div data-slot="newsletter-band" className={clsxm(newsletterBandClasses, className)} {...props} />
);

/** The two lines above the field, set tighter to each other than to the field. */
const newsletterBandCopyClasses = 'flex flex-col gap-2';

export type NewsletterBandCopyProps = ComponentProps<'div'>;

/** The band's copy block. */
export const NewsletterBandCopy = ({ className, ...props }: NewsletterBandCopyProps) => (
  <div
    data-slot="newsletter-band-copy"
    className={clsxm(newsletterBandCopyClasses, className)}
    {...props}
  />
);

/**
 * The ask, in the brand's display face.
 *
 * A neutral element rather than a heading: a footer band appears on every route
 * and a heading inside it would enter every page's outline. A page that wants
 * this text in its own composes `@vs-shop/ui/heading` around it.
 */
const newsletterBandTitleClasses = ['font-heading', 'lap:text-2xl text-xl tracking-tight'];

export type NewsletterBandTitleProps = ComponentProps<'span'>;

/** The band's headline. */
export const NewsletterBandTitle = ({ className, ...props }: NewsletterBandTitleProps) => (
  <span
    data-slot="newsletter-band-title"
    className={clsxm(newsletterBandTitleClasses, className)}
    {...props}
  />
);

/** The line under it — quieter, wider-set, and the band's only tracked type. */
const newsletterBandSubtitleClasses =
  'text-foreground-muted-inverted lap:text-lg/6 text-xs/4 tracking-[0.135em]';

export type NewsletterBandSubtitleProps = ComponentProps<'span'>;

/** The band's supporting line. */
export const NewsletterBandSubtitle = ({ className, ...props }: NewsletterBandSubtitleProps) => (
  <span
    data-slot="newsletter-band-subtitle"
    className={clsxm(newsletterBandSubtitleClasses, className)}
    {...props}
  />
);

/**
 * The field and its submit as one control.
 *
 * A row rather than a gap: the two are fused at the seam — the field loses its
 * right border and the button its left rounding — so anything between them
 * would show as a crack down the middle of what reads as one object.
 */
const newsletterBandFormClasses = 'flex';

export type NewsletterBandFormProps = ComponentProps<'form'>;

/** The subscribe form. */
export const NewsletterBandForm = ({ className, ...props }: NewsletterBandFormProps) => (
  <form
    data-slot="newsletter-band-form"
    className={clsxm(newsletterBandFormClasses, className)}
    {...props}
  />
);

/**
 * The field's own box.
 *
 * Positioned, and load-bearing for that reason: the mark inside the field hangs
 * off this box, and a composition that rendered the input bare would put the
 * mark somewhere else on the page entirely.
 */
const newsletterBandFieldClasses = 'relative w-full';

export type NewsletterBandFieldProps = ComponentProps<'div'>;

/** The field's frame. */
export const NewsletterBandField = ({ className, ...props }: NewsletterBandFieldProps) => (
  <div
    data-slot="newsletter-band-field"
    className={clsxm(newsletterBandFieldClasses, className)}
    {...props}
  />
);

/**
 * The address field itself.
 *
 * Its right edge is open, because the submit closes it.
 */
export const newsletterBandInputVariants = cva(
  'border-foreground-inverted/40 bg-background text-foreground placeholder:text-foreground focus:ring-foreground-inverted/80 lap:text-[18px] h-14 w-full rounded-l-lg border border-r-0 pl-4 text-left tracking-tight focus:ring-1 focus:outline-0 focus:ring-inset focus:placeholder:text-transparent',
  {
    variants: {
      variant: {
        /** Nothing wrong with it, or nothing said yet. */
        base: '',
        /** The address the shopper typed is not one. */
        invalid: 'border-destructive focus:ring-destructive',
        /** The opt-out: a tenant painting the field itself. */
        unstyled: '',
      },
    },
  }
);

type NewsletterBandInputVariantName = NonNullable<
  VariantProps<typeof newsletterBandInputVariants>['variant']
>;

export type NewsletterBandInputVariant = NewsletterBandInputVariantName;

/**
 * The field's appearance axis, derived from its recipe and bound to it by
 * `satisfies`, so the union and the recipe cannot drift.
 */
export const NewsletterBandInputVariant = {
  /** Nothing wrong with it, or nothing said yet. */
  BASE: 'base',
  /** The address the shopper typed is not one. */
  INVALID: 'invalid',
  /** The opt-out: a tenant painting the field itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, NewsletterBandInputVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type NewsletterBandInputProps = ComponentProps<'input'> & {
  readonly variant: NewsletterBandInputVariant;
};

/**
 * The e-mail field.
 *
 * `aria-invalid` is not set here: whether the value is wrong is the tenant's
 * validation talking, and it rides the spread native props from the call site
 * that knows.
 */
export const NewsletterBandInput = ({ className, variant, ...props }: NewsletterBandInputProps) => (
  <input
    data-slot="newsletter-band-input"
    className={clsxm(newsletterBandInputVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The mark inside the field's leading edge.
 *
 * `pointer-events-none`, because it sits over the field's own click target: a
 * shopper tapping the envelope means to type, and without this they would tap
 * an inert box instead.
 */
const newsletterBandFieldMarkClasses =
  'pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 transform';

export type NewsletterBandFieldMarkProps = ComponentProps<'div'>;

/** The field's leading mark. */
export const NewsletterBandFieldMark = ({ className, ...props }: NewsletterBandFieldMarkProps) => (
  <div
    data-slot="newsletter-band-field-mark"
    className={clsxm(newsletterBandFieldMarkClasses, className)}
    {...props}
  />
);

/**
 * How the submit meets the field beside it.
 *
 * The two brands disagree, which is what makes this an axis rather than one
 * hardcoded appearance: rcprofi fuses the control to the field's right edge —
 * square on that side, sharing its border — and RacingLine stands it off as a
 * button of its own. Both are the same part in the same place; only the seam
 * between them differs.
 */
export const newsletterBandSubmitVariants = cva('', {
  variants: {
    variant: {
      /** Welded to the field: square where they meet, rounded where they do not. */
      fused: '',
      /** A control of its own, clear of the field. */
      standalone: 'lap:text-base h-14 shrink-0 rounded-lg px-8',
    },
  },
});

type NewsletterBandSubmitVariantName = NonNullable<
  VariantProps<typeof newsletterBandSubmitVariants>['variant']
>;

export type NewsletterBandSubmitVariant = NewsletterBandSubmitVariantName;

/** The submit's appearance axis, bound to its recipe by `satisfies`. */
export const NewsletterBandSubmitVariant = {
  /** Welded to the field: square where they meet, rounded where they do not. */
  FUSED: 'fused',
  /** A control of its own, clear of the field. */
  STANDALONE: 'standalone',
} as const satisfies Record<string, NewsletterBandSubmitVariantName>;

export type NewsletterBandSubmitProps = Omit<ButtonProps, 'variant'> & {
  readonly variant: NewsletterBandSubmitVariant;
};

/**
 * The control that sends the address.
 *
 * Composes `@vs-shop/ui`'s `Button`, choosing the primitive's own appearance
 * from this section's arm — so the seam a band wants is named for the LOOK here
 * rather than as a primitive variant every composition would have to remember.
 *
 * There is no `unstyled` arm, and that is not an omission: every arm here has to
 * resolve to a Button appearance, and the primitive has no face that paints
 * nothing. An arm mapping to no variant would fall through to the primitive's
 * own default — an appearance decided by absence, which is the exact thing a
 * named opt-out exists to prevent.
 */
const submitButtonVariant = {
  fused: 'newsletterInput',
  standalone: 'primary',
} as const satisfies Record<NewsletterBandSubmitVariant, ButtonProps['variant']>;

/** The subscribe control. */
export const NewsletterBandSubmit = ({
  className,
  variant,
  ...props
}: NewsletterBandSubmitProps) => (
  <Button
    type="submit"
    data-slot="newsletter-band-submit"
    variant={submitButtonVariant[variant]}
    className={clsxm(newsletterBandSubmitVariants({ variant }), className)}
    {...props}
  />
);

/** What the shopper agrees to by subscribing — the quietest line in the band. */
const newsletterBandConsentClasses = [
  'font-heading',
  'text-foreground-muted-inverted text-xs/4 tracking-tight',
];

export type NewsletterBandConsentProps = ComponentProps<'div'>;

/** The consent line. */
export const NewsletterBandConsent = ({ className, ...props }: NewsletterBandConsentProps) => (
  <div
    data-slot="newsletter-band-consent"
    className={clsxm(newsletterBandConsentClasses, className)}
    {...props}
  />
);

/** What the submit came back with. */
export const newsletterBandNoticeVariants = cva('font-medium', {
  variants: {
    variant: {
      /** The address was taken and a confirmation is on its way. */
      accepted: 'text-success',
      /** It was not. */
      rejected: 'text-destructive',
      /** The opt-out: a tenant painting the line itself. */
      unstyled: '',
    },
  },
});

type NewsletterBandNoticeVariantName = NonNullable<
  VariantProps<typeof newsletterBandNoticeVariants>['variant']
>;

export type NewsletterBandNoticeVariant = NewsletterBandNoticeVariantName;

/** The outcome line's appearance axis, bound to its recipe by `satisfies`. */
export const NewsletterBandNoticeVariant = {
  /** The address was taken and a confirmation is on its way. */
  ACCEPTED: 'accepted',
  /** It was not. */
  REJECTED: 'rejected',
  /** The opt-out: a tenant painting the line itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, NewsletterBandNoticeVariantName>;

export type NewsletterBandNoticeProps = ComponentProps<'p'> & {
  readonly variant: NewsletterBandNoticeVariant;
};

/**
 * The line under the form that says what happened.
 *
 * Rendered by the composition or not rendered at all — an outcome that has not
 * happened yet leaves no place, and the consent line above it stays where it
 * was either way.
 */
export const NewsletterBandNotice = ({
  className,
  variant,
  ...props
}: NewsletterBandNoticeProps) => (
  <p
    data-slot="newsletter-band-notice"
    className={clsxm(newsletterBandNoticeVariants({ variant }), className)}
    {...props}
  />
);
